/**
 * 契約終了 (退去処理) エントリの「契約情報を見る」ボタンから開かれる前契約者情報モーダル。
 *
 * UI は退去処理モーダル (DepartureProcess) と同じ「グレー box + ラベル + 値」パターンで
 * 全フィールドを読み取り専用表示する。
 *
 * - タブ 1: 契約情報 — ContractHistoryRecord の contractDetails を表示
 * - タブ 2: 入金管理 — Unit の全 payments を契約期間 (start_date ~ end_date) で
 *   フィルタして CustomTable に表示
 */

import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import {
  CustomModal,
  CustomTable,
  colors,
  type CustomColumnDef,
  CustomRenderCellWithDash,
} from "@ippon/shared-components";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store";
import { fetchPaymentsBySection } from "@/store/paymentSlice";
import type { PrivateAreaHistoryRecord } from "../types";

interface Props {
  open: boolean;
  onClose: () => void;
  record: PrivateAreaHistoryRecord;
  unitId: string;
}

type SubTab = "info" | "payment";

const tabConfig: Array<{ value: SubTab; label: string }> = [
  { value: "info", label: "契約情報" },
  { value: "payment", label: "入金管理" },
];

const formatYen = (n?: number | string) => {
  if (n === undefined || n === null || n === "") return "-";
  const num = typeof n === "string" ? Number(n) : n;
  if (Number.isNaN(num)) return "-";
  return `¥${num.toLocaleString("ja-JP")}`;
};

/**
 * グレー背景の読み取り専用フィールド (DepartureProcess モーダルと同じパターン)。
 */
const ReadOnlyField: React.FC<{ label: string; value?: string }> = ({
  label,
  value,
}) => (
  <Box>
    <Typography fontSize={12} fontWeight={700} mb={0.5}>
      {label}
    </Typography>
    <Box
      sx={{
        p: 1,
        background: "#f3f4f6",
        borderRadius: "8px",
        border: "1px solid #D9D9D9",
      }}
    >
      <Typography fontSize={12} sx={{ minHeight: "1.2em" }}>
        {value || "-"}
      </Typography>
    </Box>
  </Box>
);

const PreviousContractorModal: React.FC<Props> = ({
  open,
  onClose,
  record,
  unitId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [activeTab, setActiveTab] = useState<SubTab>("info");
  const cd = (record.contractDetails || {}) as any;
  const tenantName = record.tenantName || cd.tenantName || "-";
  const contractorType: "individual" | "corporate" =
    cd.contractorType === "corporate" ? "corporate" : "individual";

  // 入金履歴は payments slice (Unit ベースの全件) から取得し、契約期間でフィルタ
  const allPayments = useSelector(
    (s: RootState) => (s as any).payments?.list?.data || [],
  ) as any[];

  useEffect(() => {
    if (!open || !unitId) return;
    void dispatch(fetchPaymentsBySection({ propertySectionId: unitId }));
  }, [open, unitId, dispatch]);

  const filteredPayments = useMemo(() => {
    if (!Array.isArray(allPayments)) return [];
    if (!cd.startDate && !cd.endDate) return allPayments;
    return allPayments.filter((p) => {
      const d = p?.scheduled_date || p?.transfer_datetime;
      if (!d) return false;
      const dStr = String(d).slice(0, 10);
      if (cd.startDate && dStr < cd.startDate) return false;
      if (cd.endDate && dStr > cd.endDate) return false;
      return true;
    });
  }, [allPayments, cd.startDate, cd.endDate]);

  const paymentRows = useMemo(
    () =>
      filteredPayments.map((p, idx) => ({
        id: String(p.payment_id || idx),
        scheduled_date: p.scheduled_date || "",
        items: Array.isArray(p.items)
          ? Array.from(
              new Set(
                p.items
                  .map((it: any) => String(it?.name || ""))
                  .filter(Boolean),
              ),
            )
          : ([] as string[]),
        amount: Array.isArray(p.items)
          ? p.items.reduce(
              (sum: number, it: any) => sum + Number(it.amount || 0),
              0,
            )
          : Number(p.amount || 0),
        transfer_method: p.transfer_method || "",
        status: p.status || "",
      })),
    [filteredPayments],
  );

  const paymentColumns: CustomColumnDef[] = useMemo(
    () => [
      {
        field: "scheduled_date",
        headerName: "支払期日",
        flex: 1,
        sortable: false,
        renderCell: CustomRenderCellWithDash,
      },
      {
        field: "items",
        headerName: "明細項目",
        flex: 1.4,
        sortable: false,
        // 収入一覧/入金管理一覧と同じグレーチップで科目名を表示 (改行なし1行)。
        renderCell: ({ row }) => {
          const names: string[] = Array.isArray(row.items) ? row.items : [];
          if (names.length === 0) {
            return (
              <Box component="span" sx={{ color: "#999", fontSize: 14 }}>
                -
              </Box>
            );
          }
          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "nowrap",
                alignItems: "center",
                gap: 0.5,
                py: 0.5,
                minWidth: 0,
                width: "100%",
                overflow: "hidden",
              }}
            >
              {names.map((n, i) => (
                <Box
                  key={`${i}-${n}`}
                  sx={{
                    bgcolor: "#F0F0F0",
                    display: "inline-flex",
                    alignItems: "center",
                    height: 24,
                    px: 1,
                    borderRadius: "4px",
                    fontSize: 12,
                    color: "#3E3E3E",
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  {n}
                </Box>
              ))}
            </Box>
          );
        },
      },
      {
        field: "amount",
        headerName: "請求額",
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => (
          <Typography fontSize={14}>{formatYen(row.amount)}</Typography>
        ),
      },
      {
        field: "transfer_method",
        headerName: "入金方法",
        flex: 1,
        sortable: false,
        renderCell: CustomRenderCellWithDash,
      },
      {
        field: "status",
        headerName: "ステータス",
        flex: 1,
        sortable: false,
        renderCell: CustomRenderCellWithDash,
      },
    ],
    [],
  );

  const total = useMemo(
    () =>
      paymentRows
        .filter((r) => r.status === "支払済" || r.status === "入金済")
        .reduce((sum, r) => sum + Number(r.amount || 0), 0),
    [paymentRows],
  );

  // 契約者名 (個人: tenantName、法人: 法人名 — cd.tenantName に法人名が入っている設計)
  const tenantLabel = contractorType === "corporate" ? "法人名" : "契約者氏名";

  return (
    <CustomModal
      openModal={open}
      handleCloseModal={onClose}
      title={`前契約者情報 - ${tenantName}`}
      modalType="registerModal"
      addTitleBorder
    >
      <Box className="registerModalBody">
        {/* タブ — DetailPageHeader と同じ MUI Tabs スタイル */}
        <Box sx={{ backgroundColor: "white", mb: 2 }}>
          <Tabs
            value={tabConfig.findIndex((t) => t.value === activeTab)}
            onChange={(_e, idx) => setActiveTab(tabConfig[idx].value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: "30px",
              borderBottom: `1px solid ${colors.border.tab}`,
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTab-root": {
                position: "relative",
                minHeight: "30px",
                paddingTop: 0.5,
                paddingBottom: 0.5,
                paddingLeft: 2,
                paddingRight: 2,
                minWidth: "90px",
                textTransform: "none",
                fontFamily: '"Noto Sans JP"',
                fontSize: "14px",
                fontWeight: 700,
                lineHeight: "1.7",
                color: colors.text.secondary,
                "&:hover": {
                  color: colors.primary.main,
                  opacity: 1,
                },
                "&.Mui-selected": {
                  color: colors.primary.main,
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "2px",
                    backgroundColor: colors.primary.main,
                    borderRadius: "10px 10px 0 0",
                  },
                },
              },
              "& .MuiTabs-scrollButtons": {
                color: colors.primary.main,
                "&.Mui-disabled": { opacity: 0.3 },
              },
            }}
          >
            {tabConfig.map((t) => (
              <Tab key={t.value} label={t.label} />
            ))}
          </Tabs>
        </Box>

        {/* 契約情報タブ — 全フィールド ReadOnlyField (DepartureProcess と同じグレー box) */}
        {activeTab === "info" && (
          <Stack spacing={2}>
            <ReadOnlyField
              label="契約者種別"
              value={contractorType === "corporate" ? "法人" : "個人"}
            />
            <ReadOnlyField label={tenantLabel} value={cd.tenantName} />
            <ReadOnlyField label="電話番号" value={cd.tenantPhone} />
            <ReadOnlyField label="メールアドレス" value={cd.tenantEmail} />
            <ReadOnlyField label="契約開始日" value={cd.startDate} />
            <ReadOnlyField label="契約終了日" value={cd.endDate} />
            <ReadOnlyField label="退去日" value={record.departureDate} />
            <ReadOnlyField label="契約形態" value={cd.contractForm} />
            <ReadOnlyField label="賃料起算日" value={cd.rentStartDate} />
            <ReadOnlyField label="解約予定日" value={cd.cancellationDate} />
            <ReadOnlyField
              label="月額賃料(税別)"
              value={formatYen(cd.monthlyRent)}
            />
            <ReadOnlyField
              label="料金体系"
              value={
                cd.pricingType === "unit_quantity"
                  ? "単価 × 数量"
                  : cd.pricingType === "fixed"
                    ? "固定月額"
                    : "-"
              }
            />
            {cd.pricingType === "unit_quantity" && (
              <>
                <ReadOnlyField label="単価" value={formatYen(cd.unitPrice)} />
                <ReadOnlyField
                  label="数量"
                  value={
                    cd.quantity === undefined || cd.quantity === null
                      ? "-"
                      : `${cd.quantity}${cd.quantityUnit || ""}`
                  }
                />
              </>
            )}
            <ReadOnlyField
              label="共益費"
              value={formatYen(cd.maintenanceFee)}
            />
            <ReadOnlyField label="敷金" value={formatYen(cd.deposit)} />
            <ReadOnlyField
              label="償却後敷金"
              value={formatYen(cd.depositAfter)}
            />
            <ReadOnlyField label="礼金" value={formatYen(cd.keyMoney)} />
            {Array.isArray(cd.otherFees) && cd.otherFees.length > 0 && (
              <Box>
                <Typography fontSize={12} fontWeight={700} mb={0.5}>
                  その他料金
                </Typography>
                <Stack spacing={1}>
                  {cd.otherFees.map((f: any, i: number) => (
                    <Stack
                      key={`fee-${i}`}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{
                        p: 1,
                        background: "#f3f4f6",
                        border: "1px solid #D9D9D9",
                        borderRadius: "8px",
                      }}
                    >
                      <Typography fontSize={12} sx={{ flex: 1 }}>
                        {f?.label || "-"}
                      </Typography>
                      <Typography fontSize={12} fontWeight={700}>
                        {formatYen(f?.amount)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
            <ReadOnlyField label="退去理由" value={record.departureReason} />
            <ReadOnlyField label="備考" value={cd.remarks} />
          </Stack>
        )}

        {/* 入金管理タブ */}
        {activeTab === "payment" && (
          <Stack spacing={2}>
            <Typography fontSize={14} color="#6B7280">
              契約期間 ({cd.startDate || "-"} 〜 {cd.endDate || "-"}) の入金記録
              ({paymentRows.length}件)
            </Typography>
            <CustomTable
              rows={paymentRows}
              columns={paymentColumns}
              hidePagination
              minHeight="auto"
              outlined
              noDataMessage="入金記録が登録されていません"
            />
            <Box
              sx={{
                p: 1.5,
                backgroundColor: "#F9FAFB",
                borderRadius: "8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography fontSize={14} color="#6B7280">
                入金済み合計
              </Typography>
              <Typography fontSize={16} fontWeight={700}>
                {formatYen(total)}
              </Typography>
            </Box>
          </Stack>
        )}
      </Box>

      <Box className="registerModalFooter">
        <Typography fontSize={12} color="#6B7280">
          このデータは退去時 ({record.departureDate || "-"})
          にアーカイブされました
        </Typography>
      </Box>
    </CustomModal>
  );
};

export default PreviousContractorModal;
